// OrdenesAnalitica.js — Centro de Control de Operaciones (pestaña
// "Analítica" de Órdenes de trabajo).
//
// Toda la lógica de negocio (agregados, comparación con el periodo
// anterior, percentiles de resolución, reglas de estado operativo) vive en
// el backend (getOperacionesKPIs.js) — este componente solo presenta el
// resultado ya calculado y resuelve el drill-down en cliente a partir del
// `backlogDetalle` que ya viene en la respuesta (así un clic en un KPI o en
// una franja del histograma no dispara ninguna petición nueva).
//
// Límites de datos reales de esta empresa (no se fabrica lo que no existe):
// no hay "tipo de trabajo" ni "capacidad de técnico" fiables para el
// backlog sincronizado de StelOrder (solo se rellenan en órdenes creadas a
// mano en la app) — por eso no hay panel de "Work Mix" ni un % de
// capacidad por técnico, solo conteos reales.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { authService } from '../../services/authService';
import { ESTADO_CFG, TECNICO_BADGE, TECNICO_DOT } from './OrdenesView';

const PERIODOS = [
  { key: 'hoy', label: 'Hoy' },
  { key: '7d', label: 'Últimos 7 días' },
  { key: '30d', label: 'Últimos 30 días' },
  { key: 'ano', label: 'Este año' },
];

const NIVEL_CFG = {
  estable: { label: 'OPERACIONES ESTABLES', dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
  atencion: { label: 'REQUIERE ATENCIÓN', dot: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
  critico: { label: 'CRÍTICO', dot: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50 border-red-200' },
};

// "gestionada" no es un estado de orden (ESTADO_CFG, de OrdenesView.js, no
// lo tiene) — es una incidencia sin convertir en orden todavía, de la
// colección `incidencias`. Se etiqueta aparte para no ensuciar ESTADO_CFG,
// que es de órdenes reales.
function cfgEstado(estado) {
  if (estado === 'gestionada') return { label: 'Incidencia sin agendar', cls: 'bg-purple-100 text-purple-700' };
  return ESTADO_CFG[estado] || { label: estado, cls: 'bg-gray-100 text-gray-600' };
}

function fCls() { return 'text-xs font-semibold border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700'; }

const fmtEur = (n) => `${(n || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`;

function Flecha({ pct }) {
  if (pct === null || pct === undefined || pct === 0) return <span className="text-gray-400">→ sin cambio</span>;
  const sube = pct > 0;
  return <span className={sube ? 'text-red-600' : 'text-emerald-600'}>{sube ? '↑' : '↓'} {Math.abs(pct)}% vs periodo anterior</span>;
}

function KPICard({ label, value, unidad, delta, deltaEsMejorSiBaja, sub, alert, warn, onClick }) {
  let deltaNode = null;
  if (delta !== null && delta !== undefined) {
    const sube = delta > 0;
    const esMalo = deltaEsMejorSiBaja ? sube : !sube;
    const color = delta === 0 ? 'text-gray-400' : esMalo ? 'text-red-600' : 'text-emerald-600';
    deltaNode = <span className={`text-xs font-bold ${color}`}>{delta === 0 ? '→' : sube ? '↑' : '↓'} {Math.abs(delta)}% vs anterior</span>;
  }
  // `alert` (rojo) tiene prioridad sobre `warn` (ámbar) si por lo que sea
  // se pasan los dos a la vez — nunca deberían solaparse en la práctica.
  const anillo = alert ? 'ring-2 ring-red-400' : warn ? 'ring-2 ring-amber-400' : '';
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`text-left bg-white rounded-2xl border shadow-sm p-4 w-full ${onClick ? 'hover:shadow-md transition-shadow cursor-pointer' : 'cursor-default'} ${anillo}`}
    >
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-3xl font-black text-gray-900 leading-none mt-1.5">{value}{unidad && <span className="text-base font-bold text-gray-400 ml-1">{unidad}</span>}</p>
      {deltaNode && <p className="mt-1">{deltaNode}</p>}
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </button>
  );
}

// Caja de "consejo" — un veredicto en una frase a partir de los datos ya
// calculados, para que el gráfico se entienda de un vistazo sin tener que
// interpretar barras. Mismo criterio en los 5 gráficos/listas de abajo.
function ConsejoBox({ children }) {
  if (!children) return null;
  return <div className="mt-3 bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 text-xs text-blue-800">{children}</div>;
}

function consejoTendenciaDiaria(entradas, completadas) {
  if (entradas > completadas) return '📈 Entran más órdenes de las que se cierran — el backlog está creciendo.';
  if (completadas > entradas) return '📉 Se cierran más órdenes de las que entran — el backlog se está reduciendo.';
  return '➡️ Entradas y cierres van al mismo ritmo.';
}
// Compara el promedio de la primera mitad de las semanas contra la segunda
// mitad — con pocas semanas la comparación no dice nada fiable, por eso el
// mínimo de 4.
function consejoTendenciaSemanal(tendenciaSemanal) {
  if (!tendenciaSemanal || tendenciaSemanal.length < 4) return null;
  const mitad = Math.floor(tendenciaSemanal.length / 2);
  const promedio = (arr, campo) => arr.reduce((s, x) => s + x[campo], 0) / arr.length;
  const primeras = tendenciaSemanal.slice(0, mitad);
  const ultimas = tendenciaSemanal.slice(-mitad);
  const balanceAntes = promedio(primeras, 'entradas') - promedio(primeras, 'completadas');
  const balanceDespues = promedio(ultimas, 'entradas') - promedio(ultimas, 'completadas');
  const diferencia = balanceDespues - balanceAntes;
  if (diferencia > 3) return '📈 El ritmo de entradas crece más rápido que el de cierres en las últimas semanas — vigilar que el backlog no se dispare.';
  if (diferencia < -3) return '📉 Últimamente se cierra más de lo que entra — el backlog debería estar bajando.';
  return '➡️ El equilibrio entre entradas y cierres se mantiene estable en las últimas semanas.';
}
function consejoAging(aging, kpisAging) {
  const total = aging.reduce((s, f) => s + f.count, 0);
  if (total === 0) return '✅ No hay backlog pendiente ahora mismo con estos filtros.';
  const viejas = (aging.find(f => f.id === '15-30')?.count || 0) + (aging.find(f => f.id === '30+')?.count || 0);
  const pctViejas = Math.round((viejas / total) * 100);
  if (pctViejas >= 40) return `⚠ El ${pctViejas}% del backlog pendiente lleva más de 15 días esperando — conviene revisar qué se ha quedado atascado.`;
  if (kpisAging.pct < 15) return '✅ La mayoría del backlog pendiente es reciente — buen ritmo de gestión.';
  return `El ${kpisAging.pct}% del backlog pendiente lleva más de ${kpisAging.umbralDias} días abierto.`;
}
function consejoBottlenecks(bottlenecks, base) {
  if (!bottlenecks.length) return '✅ Sin cuellos de botella detectados.';
  const top = bottlenecks[0]; // ya viene ordenado de mayor a menor
  const pct = Math.round((top.count / (base || 1)) * 100);
  return `🔍 El mayor cuello de botella es "${top.label}" (${top.count} · ${pct}%) — el primer sitio donde mirar para reducir el backlog.`;
}
function consejoPorEstado(estadosOrdenados, total) {
  if (!estadosOrdenados.length || !total) return null;
  const [est, count] = estadosOrdenados[0];
  const pct = Math.round((count / total) * 100);
  return `La mayor parte del backlog está en "${cfgEstado(est).label}" (${pct}%).`;
}

// Toggle de tipo de gráfico — mismo componente para los dos gráficos de
// tendencia (diario y de 12 semanas), pedido explícito del usuario.
function ToggleTipoGrafico({ tipo, onChange }) {
  return (
    <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5">
      {[['barras', 'Barras'], ['lineas', 'Líneas']].map(([v, label]) => (
        <button key={v} onClick={() => onChange(v)}
          className={`px-2.5 py-1 text-[11px] font-bold rounded-md transition-colors ${tipo === v ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

// Marcas "redondas" del eje Y (0, 5, 10... no 0, 7, 14 según caiga el
// máximo real) — mismo criterio que cualquier gráfico de ejes: un vistazo
// a la marca más alta ya dice cuántas órdenes son, sin tener que pasar el
// ratón. Con máximos pequeños (≤4) se numera 0..max de uno en uno.
function calcularTicksY(maxReal) {
  if (maxReal <= 4) {
    const ticks = [];
    for (let v = 0; v <= maxReal; v++) ticks.push(v);
    return { ticks, arriba: maxReal || 1 };
  }
  const pasoBruto = maxReal / 4;
  const magnitud = Math.pow(10, Math.floor(Math.log10(pasoBruto)));
  const normalizado = pasoBruto / magnitud;
  const paso = Math.max(1, Math.round((normalizado <= 1 ? 1 : normalizado <= 2 ? 2 : normalizado <= 5 ? 5 : 10) * magnitud));
  const arriba = Math.ceil(maxReal / paso) * paso;
  const ticks = [];
  for (let v = 0; v <= arriba; v += paso) ticks.push(v);
  return { ticks, arriba };
}

// Barra horizontal con valor a la derecha — usada por TarjetaTecnico para
// comparar backlog/completadas entre técnicos de un vistazo. El mínimo del
// 4% cuando valor>0 es para que un valor bajo (p.ej. 1 de un máximo de 30)
// siga siendo visible como una barra, no una línea invisible.
function BarraComparativa({ label, valor, max, colorClase, sublabel }) {
  const pct = max > 0 ? Math.max(valor > 0 ? 4 : 0, Math.round((valor / max) * 100)) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[11px] font-semibold text-gray-500">{label}</span>
        <span className="text-sm font-bold text-gray-800">
          {valor}{sublabel && <span className="text-[10px] font-normal text-gray-400"> {sublabel}</span>}
        </span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${colorClase}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// Chip de estado — solo se pinta cuando hay algo que contar (un técnico sin
// urgentes/alertas/ausencias no lleva chip vacío ocupando sitio).
function Chip({ tono, children }) {
  const TONOS = {
    rojo:      'bg-red-100 text-red-700',
    ambar:     'bg-amber-100 text-amber-700',
    verde:     'bg-emerald-100 text-emerald-700',
    gris:      'bg-gray-100 text-gray-600',
  };
  return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${TONOS[tono]}`}>{children}</span>;
}

function TarjetaTecnico({ t, maxBacklog, maxCompletadas, onSelect }) {
  const backlogAlerta = t.antiguedadMedia >= 7 || t.alertas > 0;
  return (
    <div className="border border-gray-100 rounded-xl p-3.5 hover:border-gray-200 transition-colors">
      <button onClick={() => onSelect(t.nombre)} className="flex items-center gap-1.5 mb-3 hover:opacity-75" title={`Filtrar todo el dashboard por ${t.nombre}`}>
        <span className={`w-2.5 h-2.5 rounded-full ${TECNICO_DOT[t.nombre]}`} />
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${TECNICO_BADGE[t.nombre]}`}>{t.nombre}</span>
      </button>

      <div className="space-y-2.5">
        <BarraComparativa label="Completadas (periodo)" valor={t.completadas} max={maxCompletadas} colorClase="bg-emerald-500" />
        <BarraComparativa label="Backlog actual" valor={t.backlog} max={maxBacklog}
          colorClase={backlogAlerta ? 'bg-red-500' : 'bg-gray-400'}
          sublabel={t.backlog ? `· ${t.antiguedadMedia}d medio` : ''} />
      </div>

      <div className="flex flex-wrap gap-1.5 mt-3">
        {t.urgentes > 0 && <Chip tono="rojo">🔥 {t.urgentes} urgente{t.urgentes !== 1 ? 's' : ''}</Chip>}
        {t.alertas > 0 && <Chip tono="rojo">⚠️ {t.alertas} en alerta</Chip>}
        {t.ausentes > 0 && <Chip tono="ambar">🚫 {t.ausentes} ausencia{t.ausentes !== 1 ? 's' : ''}</Chip>}
        {t.resolucion.media !== null && (
          <span title={t.resolucion.muestra ? `mediana ${t.resolucion.mediana}d · n=${t.resolucion.muestra}` : ''}>
            <Chip tono={t.resolucion.media >= 5 ? 'rojo' : t.resolucion.media >= 3 ? 'ambar' : 'gris'}>⏱ {t.resolucion.media}d resolución</Chip>
          </span>
        )}
        {t.pctFirma !== null && <Chip tono="gris">✍️ {t.pctFirma}% con firma</Chip>}
        {t.rutaCerroHoy !== null && (
          <Chip tono={t.rutaCerroHoy ? 'verde' : 'ambar'}>{t.rutaCerroHoy ? '✓ ruta cerrada hoy' : '✗ sin cerrar hoy'}</Chip>
        )}
        {t.comisionesPendientesEur > 0 && <Chip tono="ambar">💰 {fmtEur(t.comisionesPendientesEur)} pend.</Chip>}
      </div>
    </div>
  );
}

// Gráfico de tendencia compartido (diario y de 12 semanas) — barras o
// líneas (SVG, sin puntos para no deformarse con preserveAspectRatio=none),
// con eje Y (marcas + líneas de guía) y una capa de carriles invisible
// para hover: pasar el ratón por cualquier punto muestra su detalle exacto
// debajo, en vez de tener que leer alturas de barra a ojo.
function TendenciaChart({ puntos }) {
  const [tipo, setTipo] = useState('barras');
  const [hoverIdx, setHoverIdx] = useState(null);
  const maxReal = Math.max(1, ...puntos.flatMap(p => [p.entradas, p.completadas]));
  const { ticks, arriba: max } = calcularTicksY(maxReal);
  const ticksDesc = [...ticks].reverse(); // de arriba a abajo, para pintar de arriba hacia abajo
  const activo = hoverIdx !== null ? puntos[hoverIdx] : null;
  const n = puntos.length;
  // Una línea necesita 2 puntos para dibujarse — con "Hoy" (un solo día)
  // el modo líneas se vería vacío, así que ni se ofrece el toggle.
  const tipoEfectivo = n < 2 ? 'barras' : tipo;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1 text-gray-500"><span className="w-2.5 h-2.5 rounded-full bg-indigo-400 inline-block" />Entradas</span>
          <span className="flex items-center gap-1 text-gray-500"><span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" />Completadas</span>
        </div>
        {n >= 2 && <ToggleTipoGrafico tipo={tipo} onChange={setTipo} />}
      </div>
      <div className="flex gap-2 mt-2">
        {/* Eje Y */}
        <div className="flex flex-col justify-between h-32 text-[9px] text-gray-400 text-right flex-shrink-0 w-5">
          {ticksDesc.map(t => <span key={t}>{t}</span>)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="relative h-32">
            {/* Líneas de guía a la altura de cada marca del eje Y */}
            <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
              {ticksDesc.map((t, i) => <div key={i} className={`w-full border-t ${t === 0 ? 'border-gray-200' : 'border-gray-100'}`} />)}
            </div>
            {tipoEfectivo === 'lineas' ? (
              <svg viewBox={`0 0 ${Math.max(n - 1, 1) * 10} 100`} preserveAspectRatio="none" className="w-full h-full">
                <polyline points={puntos.map((p, i) => `${i * 10},${100 - (p.entradas / max) * 100}`).join(' ')} fill="none" stroke="#818cf8" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                <polyline points={puntos.map((p, i) => `${i * 10},${100 - (p.completadas / max) * 100}`).join(' ')} fill="none" stroke="#ef4444" strokeWidth="2" vectorEffect="non-scaling-stroke" />
              </svg>
            ) : (
              <div className="flex items-end gap-2 h-full">
                {puntos.map((p, i) => {
                  const hEnt = Math.round((p.entradas / max) * 100);
                  const hCom = Math.round((p.completadas / max) * 100);
                  return (
                    <div key={i} className="flex-1 flex items-end justify-center gap-0.5 h-full">
                      <div className="flex-1 bg-indigo-100 rounded-t-[3px] flex flex-col justify-end" style={{ height: `${Math.max(hEnt, p.entradas > 0 ? 3 : 0)}%` }}>
                        <div className="w-full h-full bg-indigo-400 rounded-t-[3px]" />
                      </div>
                      <div className="flex-1 bg-red-50 rounded-t-[3px] flex flex-col justify-end" style={{ height: `${Math.max(hCom, p.completadas > 0 ? 3 : 0)}%` }}>
                        <div className="w-full h-full bg-red-500 rounded-t-[3px]" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Carriles de hover — iguales en ambos modos, así el detalle
                funciona igual con barras o con líneas. */}
            <div className="absolute inset-0 flex">
              {puntos.map((_, i) => (
                <div key={i} className="flex-1 cursor-pointer" onMouseEnter={() => setHoverIdx(i)} onMouseLeave={() => setHoverIdx(null)} />
              ))}
            </div>
          </div>
          <div className="flex gap-2 mt-1">
            {puntos.map((p, i) => (
              <span key={i} className={`flex-1 text-center text-[9px] uppercase whitespace-nowrap ${hoverIdx === i ? 'text-gray-900 font-bold' : 'text-gray-400'}`}>{p.label}</span>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-2 text-xs h-4">
        {activo ? (
          <span className="text-gray-700"><strong>{activo.label}</strong>: <span className="text-indigo-600 font-semibold">{activo.entradas} entradas</span> · <span className="text-red-600 font-semibold">{activo.completadas} completadas</span></span>
        ) : (
          <span className="text-gray-300">Pasa el ratón por el gráfico para ver el detalle exacto.</span>
        )}
      </div>
    </div>
  );
}

export default function OrdenesAnalitica({ onIrATodas, onAbrirOrden }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('7d');
  const [tecnico, setTecnico] = useState('todos');
  const [estado, setEstado] = useState('todos');
  const [zona, setZona] = useState('todos');
  const [drill, setDrill] = useState(null); // { titulo, filtro: (o)=>bool } — panel de drill-down
  // "En cola para asignar" (pedido del usuario 2026-10-08): mismo número
  // que "Consultar cola" en Rutas (getColaBacklog.js) — pendiente/ausente/
  // gestionada, sin zombies — para no tener que ir a Rutas solo para verlo.
  // Se pide aparte (no es un KPI que calcule este backend) para no
  // duplicar esa lógica aquí y arriesgarse a que los dos acaben
  // desincronizados — un solo número, una sola fuente. No depende de
  // periodo/técnico/zona (la cola no se filtra así), por eso vive en su
  // propio estado en vez de dentro de `data`.
  const [colaTotal, setColaTotal] = useState(null);
  const [colaError, setColaError] = useState(false);

  const cargarCola = useCallback(async () => {
    try {
      const r = await fetch('/.netlify/functions/getColaBacklog?page=1&pageSize=1', { headers: { ...authService.authHeader() } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Error al cargar la cola');
      setColaTotal(d.total);
      setColaError(false);
    } catch {
      setColaError(true);
    }
  }, []);

  // `forzar`: el botón "↻ Actualizar" quiere datos frescos DE VERDAD — la
  // respuesta lleva Cache-Control de 3 minutos (pensado para no recargar el
  // backend al cambiar de filtro rápido, no para ignorar un clic explícito
  // en "Actualizar"). Sin bypass, pulsar el botón antes de esos 3 minutos
  // podía devolver la misma respuesta cacheada sin ningún aviso.
  const cargar = useCallback(async (forzar = false) => {
    setLoading(true); setError('');
    try {
      const qs = new URLSearchParams({ periodo, tecnico, estado, zona });
      if (forzar) qs.set('_', Date.now().toString());
      const r = await fetch(`/.netlify/functions/getOperacionesKPIs?${qs}`, {
        headers: { ...authService.authHeader() },
        ...(forzar ? { cache: 'no-store' } : {}),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Error al cargar');
      setData(d);
      // "Actualizar" es una acción explícita y poco frecuente del usuario —
      // a diferencia del montaje inicial, aquí sí tiene sentido refrescar
      // también la cola, pero siempre DESPUÉS de que esta petición termine
      // (nunca en paralelo, mismo motivo que el efecto de más abajo).
      if (forzar) cargarCola();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [periodo, tecnico, estado, zona, cargarCola]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { setDrill(null); }, [periodo, tecnico, estado, zona]);

  // cargarCola() SOLO al montar, encadenada DESPUÉS de que termine la carga
  // principal — nunca en paralelo con ella (regresión real 2026-10-13: las
  // dos a la vez duplicaban de golpe la ráfaga de `runQuery` contra
  // Firestore justo al entrar en Analítica —que de por sí ya lanza más de
  // una decena en paralelo— y eso bastaba para chocar con el límite de
  // ráfaga del proyecto, 429 Quota exceeded). No depende de periodo/
  // técnico/zona, así que tampoco hace falta repetirla en cada cambio de
  // filtro — el ref evita que el efecto de abajo la dispare más de una vez.
  const colaYaPedida = useRef(false);
  useEffect(() => {
    if (!data || colaYaPedida.current) return;
    colaYaPedida.current = true;
    cargarCola();
  }, [data, cargarCola]);

  const criticas = useMemo(() => {
    if (!data) return [];
    // pendiente_pago va en backlogDetalle solo para el bottleneck "Esperando
    // cobro" (ver backend) — el trabajo de campo ya está hecho, así que no
    // pinta aquí como si necesitara intervención operativa.
    return data.backlogDetalle.filter(o => o.estado !== 'pendiente_pago' && (o.enAlerta || !o.tecnicoNombre || o.urgente)).slice(0, 12);
  }, [data]);

  if (error) {
    return (
      <div className="space-y-3">
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm">{error}</div>
        <button onClick={cargar} className="text-xs font-bold text-red-600 hover:text-red-800">Reintentar</button>
      </div>
    );
  }
  if (loading || !data) {
    return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500" /></div>;
  }

  const { kpis, estadoOperativo, estadosOrdenados, porDia, tendenciaSemanal, aging, bottlenecks, tablaTecnicos, tablaZonas, opciones, backlogDetalle, zombiesDetalle, generadoEn, backlogDesde } = data;
  const nivel = NIVEL_CFG[estadoOperativo.nivel] || NIVEL_CFG.estable;
  const maxAging = Math.max(1, ...aging.map(a => a.count));
  const totalBacklog = kpis.backlog.valor;
  const horaGenerado = new Date(generadoEn).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

  const abrirDrill = (titulo, filtro) => setDrill({ titulo, lista: backlogDetalle.filter(filtro) });
  // Mismo criterio que backlogPendiente en el backend (getOperacionesKPIs.js):
  // ni pendiente_pago (visita ya hecha, solo falta cobrar — no es backlog
  // "atascado") ni una orden programada a futuro cuentan como antigüedad/
  // aging/alerta real. backlogDetalle trae ambas igualmente (para el
  // bottleneck "Esperando cobro" y para mostrar lo programado), así que
  // cualquier drill-down de "antigüedad"/"envejecido"/"en alerta" tiene que
  // excluirlas aquí también — si no, el conteo de la tarjeta (que sí las
  // excluye) no cuadra con lo que aparece al hacer clic.
  const esBacklogPendienteDeVerdad = o => o.estado !== 'pendiente_pago' && !o.programado;

  return (
    <div className="space-y-5">
      {/* Cabecera */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-black text-gray-900 text-lg flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${nivel.dot}`} />
            Centro de Control de Operaciones
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">Datos internos (Firebase, sincronizados desde StelOrder) · backlog desde {backlogDesde} · vista generada a las {horaGenerado}</p>
        </div>
        <button onClick={() => cargar(true)} className="text-xs font-bold text-gray-500 hover:text-gray-700">↻ Actualizar</button>
      </div>

      {/* Filtros globales */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex bg-gray-100 rounded-xl p-1 gap-0.5">
          {PERIODOS.map(p => (
            <button key={p.key} onClick={() => setPeriodo(p.key)}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${periodo === p.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {p.label}
            </button>
          ))}
        </div>
        <select value={tecnico} onChange={e => setTecnico(e.target.value)} className={fCls()}>
          <option value="todos">Todos los técnicos</option>
          {opciones.tecnicos.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={estado} onChange={e => setEstado(e.target.value)} className={fCls()}>
          <option value="todos">Todos los estados</option>
          {opciones.estados.map(e2 => <option key={e2} value={e2}>{cfgEstado(e2).label}</option>)}
        </select>
        {opciones.zonas.length > 0 && (
          <select value={zona} onChange={e => setZona(e.target.value)} className={fCls()}>
            <option value="todos">Todas las zonas</option>
            {opciones.zonas.map(z => <option key={z} value={z}>{z}</option>)}
          </select>
        )}
        {(tecnico !== 'todos' || estado !== 'todos' || zona !== 'todos') && (
          <button onClick={() => { setTecnico('todos'); setEstado('todos'); setZona('todos'); }} className="text-xs text-red-500 font-bold hover:text-red-700">× Limpiar filtros</button>
        )}
      </div>

      {/* Avisos de fiabilidad de datos — nunca ocultar en silencio que un
          número excluye algo conocido (p. ej. drift de sync con StelOrder). */}
      {data.avisos?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 space-y-0.5">
          {data.avisos.map((a, i) => <p key={i} className="text-xs text-amber-800">⚠ {a}</p>)}
        </div>
      )}

      {/* Estado operativo global */}
      <div className={`rounded-2xl border p-4 ${nivel.bg}`}>
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${nivel.dot}`} />
          <span className={`font-black text-sm ${nivel.text}`}>● {nivel.label}</span>
        </div>
        {estadoOperativo.indicadores.length > 0 ? (
          <ul className="mt-2 space-y-0.5">
            {estadoOperativo.indicadores.map((ind, i) => (
              <li key={i} className={`text-xs ${nivel.text}`}>· {ind}</li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-emerald-700 mt-1">Ningún indicador supera los umbrales configurados.</p>
        )}
      </div>

      {/* KPIs principales */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Antes una sola tarjeta "Backlog" mezclaba lo atascado (sin fecha
            o ya vencido) con lo que ya tiene fecha y técnico asignado — dos
            cosas con implicación muy distinta ("hay que actuar" vs "ya está
            en marcha"), pedido explícito del usuario separarlas. */}
        <KPICard label="Backlog" value={kpis.backlog.pendiente} sub="pendiente de verdad — sin programar o ya vencido" onClick={() => abrirDrill('Backlog pendiente de verdad', o => esBacklogPendienteDeVerdad(o))} />
        <KPICard label="Programadas" value={kpis.backlog.programado} sub="ya tienen fecha y técnico asignado — no están atascadas" onClick={() => abrirDrill('Órdenes programadas a futuro', o => o.programado)} />
        {/* Subconjunto de Backlog: solo lo que todavía no se puede asignar a
            ningún técnico (pendiente/ausente/gestionada, sin zombies) — el
            resto del backlog (garantía, pendiente_material...) ya tiene
            técnico o está esperando algo externo (una pieza, un
            presupuesto), no "cola de asignación" en sentido estricto. */}
        <KPICard label="En cola para asignar" value={colaError ? '—' : (colaTotal ?? '…')} sub="pendiente/ausente/gestionada sin técnico, listas para el generador de rutas" />
        {/* Rojo: entran más órdenes de las que el equipo actual puede sacar
            en este periodo (umbral escalado a 320/30d). Si además lleva 3
            bloques de 30 días seguidos así, el aviso de "valorar contratar"
            sale en el banner de Estado operativo de arriba, no aquí. */}
        <KPICard label="Entradas" value={kpis.entradas.valor} delta={kpis.entradas.cambioPct} deltaEsMejorSiBaja={false} sub={kpis.entradas.alertaContratacion ? `🔴 3 bloques de 30d seguidos > ${kpis.entradas.umbralAlto} — valorar contratar` : (kpis.entradas.anterior !== null ? `vs ${kpis.entradas.anterior} periodo anterior · umbral ${kpis.entradas.umbralAlto}` : 'año en curso')} alert={kpis.entradas.alerta || kpis.entradas.alertaContratacion} />
        {/* Rojo: menos cierres de los esperados — puede ser bajo
            rendimiento, pero también baja/vacaciones/festivo, no es
            automáticamente un problema de gestión (por eso solo es un
            aviso visual, no entra en el banner de Estado operativo). */}
        <KPICard label="Completadas" value={kpis.completadas.valor} delta={kpis.completadas.cambioPct} deltaEsMejorSiBaja={false} sub={`${kpis.completadas.anterior !== null ? `vs ${kpis.completadas.anterior} periodo anterior · ` : ''}umbral ${kpis.completadas.umbralBajo}`} alert={kpis.completadas.alerta} />
        {/* Sin incidencias en el denominador (ver backend) — de lo contrario
            la tasa queda deflactada de forma permanente por trabajo que
            nunca puede "completarse" bajo esta métrica. */}
        <KPICard label="Tasa de resolución" value={kpis.completionRate.valor !== null ? kpis.completionRate.valor : '—'} unidad={kpis.completionRate.valor !== null ? '%' : ''} sub="completadas ÷ entradas del periodo (sin incidencias)" alert={kpis.completionRate.critico} warn={kpis.completionRate.aviso} />
        {/* Fijo a los últimos 30 días naturales, SIEMPRE — no cambia con el
            filtro de periodo de arriba (pedido explícito del usuario: un
            número de referencia estable en vez de saltar según el filtro). */}
        {/* Semáforo sobre la MEDIA (no el P90): ámbar a partir de 3 días,
            rojo a partir de 5 — pedido explícito del usuario. */}
        <KPICard label="Resolución (30 días)" value={kpis.resolucion.media !== null ? kpis.resolucion.media : '—'} unidad={kpis.resolucion.media !== null ? 'd' : ''} sub={kpis.resolucion.media !== null ? `mediana ${kpis.resolucion.mediana}d · P90 ${kpis.resolucion.p90}d · n=${kpis.resolucion.muestra}` : 'sin órdenes de 2026+ resueltas en los últimos 30 días'} alert={kpis.resolucion.media !== null && kpis.resolucion.media >= 5} warn={kpis.resolucion.media !== null && kpis.resolucion.media >= 3 && kpis.resolucion.media < 5} />
        {/* critico/aviso vienen ya calculados por el backend contra
            _opsThresholds.js — antes se comparaba aquí contra un número
            hardcodeado que podía desincronizarse del umbral real. */}
        <KPICard label="Backlog envejecido" value={`${kpis.aging.pct}%`} sub={`${kpis.aging.count} de ${kpis.aging.base} pendientes > ${kpis.aging.umbralDias} días`} alert={kpis.aging.critico} warn={kpis.aging.aviso} onClick={() => abrirDrill(`Backlog > ${kpis.aging.umbralDias} días`, o => esBacklogPendienteDeVerdad(o) && o.diasAbierta > kpis.aging.umbralDias)} />
        <KPICard label="Incidencias sin agendar" value={kpis.incidenciasGestionadas.count} sub="bolsa de trabajo sin convertir en orden" alert={kpis.incidenciasGestionadas.critico} warn={kpis.incidenciasGestionadas.aviso} onClick={() => abrirDrill('Incidencias sin agendar', o => o.estado === 'gestionada')} />
        <KPICard label="En alerta SLA" value={`${kpis.alerta.pct}%`} sub={`${kpis.alerta.count} de ${kpis.alerta.base} pendientes`} alert={kpis.alerta.critico} warn={kpis.alerta.aviso} onClick={() => abrirDrill('En alerta SLA', o => esBacklogPendienteDeVerdad(o) && o.enAlerta)} />
        {/* Antes no había NINGUNA visibilidad de cuántas órdenes se
            rechazan — se descartaban en silencio de toda métrica. */}
        <KPICard label="Rechazadas" value={kpis.rechazadas.pct !== null ? `${kpis.rechazadas.pct}%` : '—'} sub={kpis.rechazadas.pct !== null ? `${kpis.rechazadas.valor} de las entradas del periodo` : 'sin intentos de entrada en el periodo'} alert={kpis.rechazadas.critico} warn={kpis.rechazadas.aviso} />
        {/* Más de 60 días naturales sin ningún movimiento real (ver
            esZombiePorAntiguedad en el backend) — ya no cuenta como
            backlog ni infla aging/alertas, se trata como si fuera
            rechazada: casi seguro está olvidada a falta de cerrarla a
            mano en StelOrder. zombiesDetalle es su propia lista, no un
            subconjunto de backlogDetalle. */}
        <KPICard label="Zombies (&gt;60d sin tocar)" value={zombiesDetalle.length} sub="tratadas como rechazadas — revisar y cerrar en StelOrder" alert={zombiesDetalle.length > 0} onClick={zombiesDetalle.length > 0 ? () => setDrill({ titulo: 'Posibles zombie — más de 60 días sin movimiento, fuera del backlog', lista: zombiesDetalle }) : undefined} />
      </div>

      {/* Tendencia de carga de trabajo */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <h3 className="font-bold text-gray-900 mb-3">Tendencia de carga de trabajo</h3>
        {Object.keys(porDia).length === 0 ? (
          <p className="text-sm text-gray-400 py-6 text-center">La tendencia día a día no está disponible para "Este año" — elige Hoy, 7 o 30 días.</p>
        ) : (
          <>
            <TendenciaChart puntos={Object.entries(porDia).map(([dia, v]) => ({
              label: new Date(dia + 'T12:00:00Z').toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' }),
              entradas: v.entradas, completadas: v.completadas,
            }))} />
            <ConsejoBox>{consejoTendenciaDiaria(kpis.entradas.valor, kpis.completadas.valor)}</ConsejoBox>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Backlog aging */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-bold text-gray-900 mb-1">Antigüedad del backlog</h3>
          <p className="text-xs text-gray-400 mb-3">
            Solo lo que ya toca (fecha programada hoy o antes) — no incluye las {kpis.backlog.programado} órdenes programadas a futuro, que todavía no están atrasadas. Clic en una franja para ver esas órdenes.
          </p>
          <div className="space-y-2">
            {aging.map(fr => {
              const pct = Math.round((fr.count / maxAging) * 100);
              return (
                <button key={fr.id} onClick={() => abrirDrill(`Backlog: ${fr.label}`, o => {
                  const [min, max] = fr.id === '30+' ? [31, Infinity] : fr.id.split('-').map(Number);
                  return esBacklogPendienteDeVerdad(o) && o.diasAbierta >= min && o.diasAbierta <= max;
                })} className="w-full flex items-center gap-3 group" disabled={fr.count === 0}>
                  <span className="text-xs text-gray-600 w-16 flex-shrink-0 text-left group-hover:text-gray-900">{fr.label}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${fr.id === '30+' || fr.id === '15-30' ? 'bg-red-400' : fr.id === '8-14' ? 'bg-amber-400' : 'bg-emerald-400'}`} style={{ width: `${Math.max(pct, fr.count > 0 ? 3 : 0)}%` }} />
                  </div>
                  <span className="text-xs font-bold text-gray-600 w-8 text-right flex-shrink-0">{fr.count}</span>
                </button>
              );
            })}
          </div>
          <ConsejoBox>{consejoAging(aging, kpis.aging)}</ConsejoBox>
        </div>

        {/* Cuellos de botella */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-bold text-gray-900 mb-1">Cuellos de botella</h3>
          <p className="text-xs text-gray-400 mb-3">Dónde se acumula el trabajo dentro del backlog actual.</p>
          {bottlenecks.length === 0 ? (
            <p className="text-sm text-gray-400">Sin cuellos de botella detectados.</p>
          ) : (
            <div className="space-y-2">
              {bottlenecks.map(b => {
                // "Esperando cobro" (pendiente_pago) no es subconjunto de
                // `totalBacklog` (se excluye a propósito del backlog) — usar
                // conPendientePago como base común evita un % roto/Infinity
                // cuando el backlog "de verdad" está vacío pero aún queda
                // algo esperando cobro.
                const pct = Math.round((b.count / (kpis.backlog.conPendientePago || 1)) * 100);
                return (
                  <button key={b.id} onClick={() => abrirDrill(b.label, o => {
                    if (b.id === 'sin_asignar') return !o.tecnicoNombre;
                    if (b.id === 'envejecidas') return esBacklogPendienteDeVerdad(o) && o.diasAbierta > kpis.aging.umbralDias;
                    return o.estado === b.id;
                  })} className="w-full flex items-center gap-3 group">
                    <span className="text-xs text-gray-600 w-40 flex-shrink-0 text-left truncate group-hover:text-gray-900">{b.label}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                      <div className="bg-orange-400 h-full rounded-full transition-all" style={{ width: `${Math.max(pct, 3)}%` }} />
                    </div>
                    <span className="text-xs font-bold text-gray-600 w-10 text-right flex-shrink-0">{b.count}</span>
                  </button>
                );
              })}
            </div>
          )}
          <ConsejoBox>{consejoBottlenecks(bottlenecks, kpis.backlog.conPendientePago)}</ConsejoBox>
        </div>
      </div>

      {/* Backlog por estado */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <h3 className="font-bold text-gray-900 mb-1">Backlog por estado</h3>
        <p className="text-xs text-gray-400 mb-3">En qué se están quedando paradas las órdenes abiertas ahora mismo.</p>
        {estadosOrdenados.length === 0 ? (
          <p className="text-sm text-gray-400">Sin órdenes abiertas con estos filtros.</p>
        ) : (
          <div className="space-y-2">
            {estadosOrdenados.map(([est, n]) => {
              const cfg = cfgEstado(est);
              const pct = Math.round(n / totalBacklog * 100);
              const barCls = cfg.cls.split(' ').find(c => c.startsWith('bg-')) || 'bg-gray-300';
              // "gestionada" son incidencias (otra colección) — "Todas" solo
              // busca en órdenes, así que en vez de un filtro que no
              // encontraría nada, abre el propio drill-down del dashboard.
              const alClic = est === 'gestionada'
                ? () => abrirDrill(cfg.label, o => o.estado === 'gestionada')
                : () => onIrATodas(est);
              return (
                <button key={est} onClick={alClic} className="w-full flex items-center gap-3 group">
                  <span className="text-xs text-gray-600 w-40 flex-shrink-0 text-left truncate group-hover:text-gray-900">{cfg.label}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                    <div className={`${barCls} h-full rounded-full transition-all`} style={{ width: `${Math.max(pct, 3)}%` }} title={`${n} órdenes (${pct}%)`} />
                  </div>
                  <span className="text-xs font-bold text-gray-600 w-16 text-right flex-shrink-0">{n} · {pct}%</span>
                </button>
              );
            })}
          </div>
        )}
        <ConsejoBox>{consejoPorEstado(estadosOrdenados, totalBacklog)}</ConsejoBox>
      </div>

      {/* Órdenes que requieren atención */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold text-gray-900">Órdenes que requieren atención</h3>
          <button onClick={() => setDrill({ titulo: 'Todas las órdenes en alerta / sin asignar / urgentes', lista: backlogDetalle.filter(o => o.estado !== 'pendiente_pago' && (o.enAlerta || !o.tecnicoNombre || o.urgente)) })} className="text-xs font-bold text-red-600 hover:text-red-800">Ver todas →</button>
        </div>
        <p className="text-xs text-gray-400 mb-3">Antiguas, sin asignar o urgentes — las más relevantes, no todo el backlog.</p>
        {criticas.length === 0 ? (
          <p className="text-sm text-gray-400">Ninguna orden requiere atención inmediata.</p>
        ) : (
          <TablaOrdenes lista={criticas} onAbrirOrden={onAbrirOrden} />
        )}
      </div>

      {/* Por técnico — tarjetas con barras en vez de una tabla de 11
          columnas: mismos datos, pero backlog y completadas se comparan de
          un vistazo (barras escaladas al máximo de cada métrica entre
          técnicos) y el resto de indicadores va en chips de color, solo
          cuando hay algo que contar. */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <h3 className="font-bold text-gray-900 mb-1">Rendimiento por técnico</h3>
        <p className="text-xs text-gray-400 mb-3">
          Backlog/antigüedad/urgentes/ruta son de ahora mismo (antigüedad y resolución solo sobre lo que ya toca); completadas/firma son del periodo seleccionado; resolución es fija a los últimos 30 días. Sin dato de capacidad/aforo en el sistema — no se muestra un ranking, solo conteos reales. Clic en un técnico para filtrar todo el dashboard por él.
        </p>
        {tablaTecnicos.length === 0 ? (
          <p className="text-sm text-gray-400">Sin datos todavía.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {tablaTecnicos.map(t => (
              <TarjetaTecnico key={t.nombre} t={t}
                maxBacklog={Math.max(1, ...tablaTecnicos.map(x => x.backlog))}
                maxCompletadas={Math.max(1, ...tablaTecnicos.map(x => x.completadas))}
                onSelect={setTecnico} />
            ))}
          </div>
        )}
      </div>

      {/* Por zona — para ver de un vistazo si alguna zona va peor que las
          demás, sin tener que filtrar de una en una con el selector de
          arriba. */}
      {tablaZonas.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-bold text-gray-900 mb-1">Backlog por zona</h3>
          <p className="text-xs text-gray-400 mb-3">Mismo criterio que "Carga por técnico": backlog/antigüedad/urgentes son de ahora mismo, resolución es fija a los últimos 30 días. Clic en una zona para filtrar todo el dashboard por ella.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-500 text-xs uppercase">
                <th className="pb-2">Zona</th>
                <th className="pb-2 text-right">Backlog</th>
                <th className="pb-2 text-right">Antigüedad media</th>
                <th className="pb-2 text-right">Urgentes</th>
                <th className="pb-2 text-right">En alerta</th>
                <th className="pb-2 text-right">Resolución</th>
              </tr></thead>
              <tbody>
                {tablaZonas.map(z => (
                  <tr key={z.zona} className="border-t border-gray-50">
                    <td className="py-2">
                      {/* "Sin zona" es una etiqueta de agrupación de esta
                          tabla, no un valor real de zona — no hay nada por lo
                          que filtrar (el filtro global compara o.zona ===
                          valor, y ninguna orden tiene o.zona === "Sin zona"). */}
                      {z.zona === 'Sin zona' ? (
                        <span className="font-semibold text-gray-500">{z.zona}</span>
                      ) : (
                        <button onClick={() => setZona(z.zona)} className="font-semibold text-gray-700 hover:text-red-600" title={`Filtrar todo el dashboard por ${z.zona}`}>{z.zona}</button>
                      )}
                    </td>
                    <td className="py-2 text-right font-semibold text-gray-700">{z.backlog}</td>
                    <td className={`py-2 text-right ${z.antiguedadMedia >= 7 ? 'text-red-600 font-bold' : 'text-gray-600'}`}>{z.backlog ? `${z.antiguedadMedia}d` : '—'}</td>
                    <td className={`py-2 text-right ${z.urgentes > 0 ? 'text-red-600 font-bold' : 'text-gray-400'}`}>{z.urgentes}</td>
                    <td className={`py-2 text-right ${z.alertas > 0 ? 'text-red-600 font-bold' : 'text-gray-400'}`}>{z.alertas} {z.backlog > 0 && `(${z.pctAlerta}%)`}</td>
                    <td className={`py-2 text-right ${z.resolucion.media !== null && z.resolucion.media >= 5 ? 'text-red-600 font-bold' : z.resolucion.media !== null && z.resolucion.media >= 3 ? 'text-amber-600 font-bold' : 'text-gray-600'}`} title={z.resolucion.muestra ? `mediana ${z.resolucion.mediana}d · n=${z.resolucion.muestra}` : ''}>
                      {z.resolucion.media !== null ? `${z.resolucion.media}d` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tendencia de 12 semanas — fija, independiente del periodo elegido
          arriba (mismo criterio que "Resolución 30 días"). Es la única
          vista de si las cosas mejoran o empeoran a más largo plazo que el
          gráfico diario de arriba (que solo cubre el periodo seleccionado,
          como mucho 30 días). */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <h3 className="font-bold text-gray-900 mb-1">Tendencia de 12 semanas</h3>
        <p className="text-xs text-gray-400 mb-3">
          Fija a los últimos ~90 días, no cambia con el filtro de periodo de arriba. Ojo: es cuánto entra y sale cada semana (flujo), no un histórico real del tamaño del backlog — eso no se puede reconstruir con los datos que hay.
        </p>
        <TendenciaChart puntos={tendenciaSemanal.map(sem => ({
          label: new Date(sem.desde + 'T12:00:00Z').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }),
          entradas: sem.entradas, completadas: sem.completadas,
        }))} />
        <ConsejoBox>{consejoTendenciaSemanal(tendenciaSemanal)}</ConsejoBox>
      </div>

      {/* Panel de drill-down */}
      {drill && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setDrill(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-gray-900">{drill.titulo} <span className="text-gray-400 font-normal">({drill.lista.length})</span></h3>
              <button onClick={() => setDrill(null)} className="text-gray-400 hover:text-gray-700 text-lg">✕</button>
            </div>
            <div className="overflow-y-auto p-4">
              {drill.lista.length === 0 ? (
                <p className="text-sm text-gray-400">Sin órdenes en esta selección.</p>
              ) : (
                <TablaOrdenes lista={drill.lista} onAbrirOrden={onAbrirOrden} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TablaOrdenes({ lista, onAbrirOrden }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-gray-500 text-xs uppercase">
          <th className="pb-2">WO</th>
          <th className="pb-2">Cliente</th>
          <th className="pb-2">Técnico</th>
          <th className="pb-2">Estado</th>
          <th className="pb-2 text-right">Días abierta</th>
          <th className="pb-2" />
        </tr></thead>
        <tbody>
          {lista.map(o => {
            const cfg = cfgEstado(o.estado);
            return (
              <tr key={o.id} className="border-t border-gray-50">
                <td className="py-2 font-mono text-xs text-gray-700">{o.referencia || o.id}</td>
                <td className="py-2 text-gray-700 truncate max-w-[160px]">{o.clienteNombre || '—'}</td>
                <td className="py-2">
                  {o.tecnicoNombre ? (
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${TECNICO_BADGE[o.tecnicoNombre]}`}>{o.tecnicoNombre}</span>
                  ) : <span className="text-xs font-bold text-red-500">Sin asignar</span>}
                </td>
                <td className="py-2"><span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${cfg.cls}`}>{cfg.label}</span></td>
                <td className={`py-2 text-right font-bold ${o.diasAbierta >= 14 ? 'text-red-600' : o.diasAbierta >= 7 ? 'text-amber-600' : 'text-gray-500'}`}>{o.diasAbierta}d</td>
                <td className="py-2 text-right">
                  {o.esIncidencia ? (
                    <span className="text-xs text-gray-400" title="Incidencia sin convertir en orden — gestiónala desde Kanban Comercial">Sin orden aún</span>
                  ) : (
                    <button onClick={() => onAbrirOrden(o.referencia || o.id)} className="text-xs font-bold text-red-600 hover:text-red-800">Abrir →</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
